import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Sentry } from '@/sentry';
import { ApiError } from '@/types';

const API_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
// IMPORTANT: We read the token from the persisted Zustand store / localStorage
// instead of calling supabase.auth.getSession(). Calling getSession() inside
// an onAuthStateChange callback (fired by Supabase on SIGNED_IN / INITIAL_SESSION)
// causes a deadlock because getSession() waits for the auth state lock.
// The AuthProvider keeps the Zustand store token in sync on every session event.
apiClient.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      // Zustand persisted store has the freshest token (updated by AuthProvider)
      const persisted = localStorage.getItem('auth-storage');
      let token: string | null = null;

      if (persisted) {
        try {
          const parsed = JSON.parse(persisted);
          token = parsed?.state?.token ?? null;
        } catch {
          // ignore parse errors
        }
      }

      // Fallback to legacy key
      if (!token) {
        token = localStorage.getItem('authToken');
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Superadmin impersonation: send target customer_id header (skip auth endpoints)
      // Backend middleware validates that only @go-ecommerce.de users may impersonate.
      const isAuthRequest = config.url?.startsWith('/auth/');
      if (!isAuthRequest) {
        const impersonationRaw = sessionStorage.getItem('impersonation');
        if (impersonationRaw) {
          try {
            const imp = JSON.parse(impersonationRaw);
            if (imp?.merchant_id) {
              config.headers['X-Impersonate-Customer-Id'] = String(
                imp.merchant_id
              );
            }
          } catch {
            // ignore parse errors
          }
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<ApiError>) => {
    console.error(
      `[apiClient] ERROR ${error.response?.status ?? 'NETWORK'} ${error.config?.url}`,
      error.response?.data ?? error.message
    );

    // Report 5xx server errors to Sentry
    if (error.response && error.response.status >= 500) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response.status,
          responseData: error.response.data,
        },
      });
    }

    if (error.response) {
      // Server responded with error
      const apiError: ApiError = {
        message:
          error.response.data.message ||
          error.response.data.error ||
          'An error occurred',
        // error_code als Fallback: Endpunkte wie der E-Mail-Verbindungstest
        // liefern ihren stabilen Grund unter diesem Namen, damit ihn der
        // Aufrufer uebersetzen kann statt eine generische Meldung zu zeigen.
        code:
          error.response.data.code ||
          error.response.data.error_code ||
          'UNKNOWN_ERROR',
        details: error.response.data.details,
      };

      // Handle 401 Unauthorized - Clear token and redirect to login
      // Avoid redirect loop on public routes that don't require auth
      if (error.response.status === 401) {
        localStorage.removeItem('authToken');
        const path = window.location.pathname;
        const isPublicRoute =
          path.startsWith('/login') ||
          path.startsWith('/register') ||
          path.startsWith('/auth/callback') ||
          path.startsWith('/portal/') ||
          path.startsWith('/admin/login');
        if (!isPublicRoute) {
          window.location.href = '/login';
        }
      }

      return Promise.reject(apiError);
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      });
    } else {
      // Error in request setup
      return Promise.reject({
        message: error.message || 'An unexpected error occurred',
        code: 'REQUEST_ERROR',
      });
    }
  }
);

// Helper function for file uploads
export const createFormData = (
  data: Record<string, unknown>,
  files?: File[]
): FormData => {
  const formData = new FormData();

  // Add regular fields
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, JSON.stringify(value));
    }
  });

  // Add files
  if (files) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  return formData;
};

// Upload config for file uploads
export const uploadConfig: AxiosRequestConfig = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  timeout: 60000, // 60 seconds for uploads
};
